import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import menuApi from "../../services/menuapi";
import AlertDialog from "../../components/modal/AlertDialog";

const RoleCreateForm = ({ show, handleClose, handleRefresh, editingRoleId }) => {
    const [roleName, setRoleName] = useState("");
    const [errors, setErrors] = useState({});
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertTitle, setAlertTitle] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dotCount, setDotCount] = useState(0);
    const [isDataLoading, setIsDataLoading] = useState(false);
    // Dot animation for button
    useEffect(() => {
        let interval;
        if (isSubmitting) {
            interval = setInterval(() => {
                setDotCount((prev) => (prev + 1) % 4);
            }, 500);
        } else {
            setDotCount(0);
        }
        return () => clearInterval(interval);
    }, [isSubmitting]);

    useEffect(() => {
        if (editingRoleId) {
            setIsDataLoading(true); // Start loading
            const fetchRoleDetails = async () => {
                try {
                    const response = await menuApi.getRoleDetails({ id: editingRoleId });
                    const roleData = response.data?.role;
                    if (roleData) {
                        setRoleName(roleData.name);
                    }
                } catch (error) {
                    console.error("Failed to fetch role details", error);
                } finally {
                    setIsDataLoading(false); // End loading
                }
            };
            fetchRoleDetails();
        } else {
            setRoleName("");
        }
    }, [editingRoleId]);

    const validateForm = () => {
        const newErrors = {};
        if (!roleName.trim()) {
            newErrors.name = "Role name is required";
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        setIsSubmitting(true);

        try {
            const payload = { name: roleName.trim() };
            if (editingRoleId) {
                payload.id = editingRoleId; // if updating
            }

            const response = await menuApi.createRole(payload);

            if (response.status === "SUCCESS") {
                setAlertTitle("Success");
                setAlertMessage(editingRoleId ? "Role updated successfully!" : "Role created successfully!");
                setIsAlertVisible(true);
            } else {
                throw new Error(response.message || "Failed to save role");
            }
        } catch (err) {
            console.error("Role save failed:", err);
            setAlertTitle("Oops!");
            setAlertMessage(err.message || "Something went wrong. Please try again.");
            setIsAlertVisible(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} className="role-form-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingRoleId ? "Edit Role" : "Create Role"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Role Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value)}
                                isInvalid={!!errors.name}
                                disabled={isDataLoading}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Button
                            type="submit"
                            variant="success"
                            disabled={isSubmitting || isDataLoading}
                        >
                            {isSubmitting
                                ? editingRoleId
                                    ? `Updating${".".repeat(dotCount)}`
                                    : `Saving${".".repeat(dotCount)}`
                                : editingRoleId
                                    ? "Update Role"
                                    : "Create Role"}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {isAlertVisible && (
                <AlertDialog
                    title={alertTitle}
                    message={alertMessage}
                    onClose={() => {
                        setIsAlertVisible(false);
                        if (alertTitle === "Success") {
                            handleRefresh();
                            handleClose();
                        }
                    }}
                />
            )}
        </>
    );
};

export default RoleCreateForm;

